// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import { addToChainAfter } from "@insite/client-framework/HandlerCreator";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    chain,
    SetProductInfo,
} from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/UpdateVariantSelection";
import { CustomAppState } from "@werner/App/Store/Reducers";

// Xng-Change: modifying function to get real time pricing when selecting variant product
export const XngLoadRealTimePricing: any = async (props: any) => {
    const { productInfo, selectedProductId: productId } = props;
    const state = props.getState();

    if (!productInfo || !productId) {
        return;
    }

    const variantIds = (state as CustomAppState).xProductDetails.parentProductVariants?.map(variant => variant.id);

    if (!variantIds?.find(id => id === productId)) {
        return;
    }

    props.dispatch(
        loadRealTimePricing({
            productPriceParameters: [productInfo],
            onSuccess: realTimePricing => {
                const pricing = realTimePricing.realTimePricingResults!.find(
                    o => o.productId === productInfo.productId,
                );
                if (pricing) {
                    props.dispatch({
                        type: "Pages/ProductDetails/CompleteLoadRealTimePricing",
                        pricing,
                    });
                } else {
                    props.dispatch({
                        type: "Pages/ProductDetails/FailedLoadRealTimePricing",
                        productId,
                    });
                }
            },
            onError: () => {
                props.dispatch({
                    type: "Pages/ProductDetails/FailedLoadRealTimePricing",
                    productId,
                });
            },
            onComplete(realTimePricingProps) {
                if (realTimePricingProps.apiResult) {
                    this.onSuccess?.(realTimePricingProps.apiResult);
                } else if (realTimePricingProps.error) {
                    this.onError?.(realTimePricingProps.error);
                }
            },
        }),
    );

    if (getSettingsCollection(props.getState()).productSettings.inventoryIncludedWithPricing) {
        await waitFor(() => !!props.pricingLoaded);
    }
};

// Xng-Change: modifying function to get real time inventory when selecting variant product
export const XngLoadRealTimeInventory: any = (props: any) => {
    if (
        !props.selectedProductId ||
        (getIsWebCrawler() &&
            !getSettingsCollection(props.getState()).websiteSettings.includeRealTimeInventoryAndPricingForSeo)
    ) {
        return;
    }

    const state = props.getState();

    const { selectedProductId: productId } = props;
    const variantIds = (state as CustomAppState).xProductDetails.parentProductVariants?.map(variant => variant.id);

    if (!variantIds?.find(id => id === productId)) {
        return;
    }
    props.dispatch(
        loadRealTimeInventory({
            productIds: [productId],
            onComplete: realTimeInventoryProps => {
                const realTimeInventory = realTimeInventoryProps?.apiResult;

                if (realTimeInventoryProps?.error) {
                    if (props.product) {
                        props.dispatch({
                            type: "Pages/ProductDetails/FailedLoadRealTimeInventory",
                            productId,
                        });
                    }
                } else if (realTimeInventory) {
                    props.dispatch({
                        type: "Pages/ProductDetails/CompleteLoadRealTimeInventory",
                        realTimeInventory,
                    });
                }

                props.inventoryLoaded = true;
            },
        }),
    );
};

addToChainAfter(chain, SetProductInfo, XngLoadRealTimePricing);
addToChainAfter(chain, XngLoadRealTimePricing, XngLoadRealTimeInventory);
